import { Card, Group, Skeleton, Text, ThemeIcon, Title } from "@mantine/core";
import { Building2 } from "lucide-react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useSnapshot } from "valtio";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { i18nStore } from "@/store/i18n";
import type { JobData } from "./demografi.types";
import { CategoryTick } from "./demografi-axis-tick";
import {
	BAR_VALUE_MARGIN_RIGHT,
	JOB_AXIS_WIDTH,
	JOB_LABEL_MAX_CHARS,
	ROW2_CHART_HEIGHT,
} from "./demografi-layout.const";

interface DemografiJobCardProps {
	jobData: JobData[];
	loading: boolean;
}

export const DemografiJobCard = ({
	jobData,
	loading,
}: DemografiJobCardProps) => {
	const t = useTranslate();
	const dark = useIsDark();
	const { tampilkanGrid } = useSnapshot(i18nStore);

	return (
		<Card
			data-ai-target="demografi.pekerjaan"
			p="md"
			radius="xl"
			withBorder
			bg={dark ? "#1E293B" : "white"}
			style={{
				borderColor: dark ? "#334155" : "white",
				boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
			}}
			h="100%"
		>
			<Group gap="xs" mb="md">
				<ThemeIcon
					color="darmasaba-navy.7"
					variant="filled"
					size="sm"
					radius="sm"
				>
					<Building2 size={14} />
				</ThemeIcon>
				<Title order={4} c={dark ? "white" : "gray.9"}>
					{t.demografiPekerjaan.demografiPekerjaan}
				</Title>
			</Group>
			{loading ? (
				<Skeleton height={ROW2_CHART_HEIGHT} radius="md" />
			) : jobData.length === 0 ? (
				<Group justify="center" align="center" h={ROW2_CHART_HEIGHT}>
					<Text size="sm" c="dimmed">
						Belum ada data demografi pekerjaan.
					</Text>
				</Group>
			) : (
				<ResponsiveContainer width="100%" height={ROW2_CHART_HEIGHT}>
					<BarChart
						data={jobData}
						layout="vertical"
						// Beri ruang agar label nilai bar terpanjang tidak terpotong.
						margin={{ right: BAR_VALUE_MARGIN_RIGHT }}
					>
						{tampilkanGrid && (
							<CartesianGrid
								strokeDasharray="3 3"
								horizontal={false}
								stroke={dark ? "#334155" : "#e5e7eb"}
							/>
						)}
						<XAxis
							type="number"
							axisLine={false}
							tickLine={false}
							tick={{
								fill: dark ? "#E2E8F0" : "#374151",
								fontSize: 12,
							}}
						/>
						<YAxis
							type="category"
							dataKey="job"
							axisLine={false}
							tickLine={false}
							// Tanpa interval={0} Recharts membuang tick berselang: 10 kategori
							// hanya menyisakan 5 label, sehingga ada bar tanpa nama kategori.
							interval={0}
							width={JOB_AXIS_WIDTH}
							tick={
								<CategoryTick
									fill={dark ? "#E2E8F0" : "#374151"}
									fontSize={12}
									maxChars={JOB_LABEL_MAX_CHARS}
								/>
							}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: dark ? "#1E293B" : "white",
								borderColor: dark ? "#334155" : "#e5e7eb",
								borderRadius: "8px",
							}}
							itemStyle={{ color: dark ? "#E2E8F0" : "#374151" }}
							labelStyle={{ color: dark ? "#E2E8F0" : "#374151" }}
						/>
						<Bar
							dataKey="total"
							fill="#396aaaff"
							radius={[0, 8, 8, 0]}
							maxBarSize={30}
						>
							<LabelList
								dataKey="total"
								position="right"
								style={{
									fill: dark ? "#E2E8F0" : "#374151",
									fontSize: 11,
								}}
							/>
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			)}
		</Card>
	);
};
